import React, { useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faCopy } from '@fortawesome/free-solid-svg-icons';
import MarkdownIt from 'markdown-it';

// Инициализация markdown парсера
const md = new MarkdownIt({
  html: false,
  breaks: true,
  linkify: true,
  typographer: true
});

const DualChatLayout = ({
  currentChat,
  model1Data,
  model2Data,
  isDualStreaming,
  onChoose,
  onCancel,
  formatMessageDate,
  copyToClipboard,
  copiedMessageId,
  currentUserMessage // Новый проп для текущего сообщения пользователя
}) => {
  const messagesEndRef1 = useRef(null);
  const messagesEndRef2 = useRef(null);

  // Автоскролл для обоих чатов
  useEffect(() => {
    if (messagesEndRef1.current) {
      messagesEndRef1.current.scrollIntoView({ behavior: 'smooth' });
    }
    if (messagesEndRef2.current) {
      messagesEndRef2.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [model1Data?.streamingText, model2Data?.streamingText]);

  // Функция для рендеринга markdown
  const renderMarkdown = (text) => {
    return { __html: md.render(text) };
  };

  // Рендер колонки модели
  const renderModelColumn = (modelData, modelKey, messagesEndRef) => {
    return (
      <div className="dual-chat-column">
        {/* Заголовок модели */}
        <div className="dual-chat-header">
          <div className="model-info">
            <h5 className="model-name">{modelData.modelName || `Модель ${modelKey}`}</h5>
            {modelData.tokenCount > 0 && (
              <span className="token-count">{modelData.tokenCount} токенов</span>
            )}
          </div>
          {modelData.streamingText && (
            <button
              className="btn btn-sm btn-link text-muted copy-btn"
              onClick={() => copyToClipboard(modelData.streamingText, `${modelKey}_dual`)}
              title="Скопировать ответ"
            >
              <FontAwesomeIcon 
                icon={copiedMessageId === `${modelKey}_dual` ? faCheck : faCopy}
                style={copiedMessageId === `${modelKey}_dual` ? {color: '#4CAF50'} : {}}
              />
            </button>
          )}
        </div>

        {/* Область сообщений модели */}
        <div className="dual-chat-messages">
          {/* Вопрос пользователя */}
          {(currentUserMessage || currentChat?.messages?.length > 0) && (
            <div className="message user">
              <div className="message-header">
                <span className="message-role">Ваш вопрос</span>
                <span className="message-time">сейчас</span>
              </div>
              <div 
                className="message-content"
                dangerouslySetInnerHTML={renderMarkdown(
                  currentUserMessage || currentChat.messages[currentChat.messages.length - 1].content
                )}
              />
            </div>
          )}

          {/* Ответ модели */}
          <div className="message assistant streaming">
            <div className="message-header">
              <span className="message-role">{modelData.modelName || `Модель ${modelKey}`}</span>
              {modelData.tokenCount > 0 && (
                <span className="message-time">{modelData.tokenCount} токенов</span>
              )}
            </div>
            
            <div className="message-content">
              {!modelData.streamingText && isDualStreaming ? (
                <div className="thinking-indicator">
                  <span>Модель думает</span>
                  <div className="thinking-dots">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              ) : modelData.streamingText ? (
                <div 
                  className="markdown-content"
                  dangerouslySetInnerHTML={renderMarkdown(modelData.streamingText)}
                />
              ) : (
                <div className="no-response">Ожидание ответа...</div>
              )}
              
              {isDualStreaming && modelData.streamingText && (
                <span className="streaming-cursor">|</span>
              )}
            </div>
          </div>
          
          <div ref={messagesEndRef} style={{ height: '20px' }} />
        </div>
      </div>
    );
  };

  return (
    <div className="dual-chat-layout fullscreen">
      {/* Заголовок с управлением */}
      <div className="dual-layout-header">
        <div className="dual-title">
          <h4>Сравнение моделей</h4>
          {isDualStreaming && (
            <span className="streaming-status">Генерация...</span>
          )}
        </div>
        
        <div className="dual-controls">
          {/* Кнопка отмены (только во время генерации) */}
          {isDualStreaming && (
            <button
              className="btn btn-sm btn-outline-danger"
              onClick={onCancel}
              title="Остановить генерацию"
            >
              Остановить
            </button>
          )}
        </div>
      </div>

      {/* Два чата рядом */}
      <div className="dual-chats-container">
        {renderModelColumn(model1Data, 'model1', messagesEndRef1)}
        <div className="dual-separator"></div>
        {renderModelColumn(model2Data, 'model2', messagesEndRef2)}
      </div>

      {/* Кнопки выбора внизу */}
      {!isDualStreaming && model1Data?.streamingText && model2Data?.streamingText && (
        <div className="dual-choice-footer">
          <div className="choice-buttons">
            <button
              className="btn btn-success choice-btn"
              onClick={() => onChoose('model1', model1Data.streamingText, model1Data.modelId)}
            >
              <FontAwesomeIcon icon={faCheck} />
              Выбрать {model1Data.modelName || 'Модель 1'}
            </button>
            
            <button
              className="btn btn-success choice-btn"
              onClick={() => onChoose('model2', model2Data.streamingText, model2Data.modelId)}
            >
              <FontAwesomeIcon icon={faCheck} />
              Выбрать {model2Data.modelName || 'Модель 2'}
            </button>
          </div>
          
          <p className="choice-hint">
            💡 Выберите лучший вариант или продолжите диалог — будет автоматически выбран первый вариант
          </p>
        </div>
      )}
    </div>
  );
};

export default DualChatLayout; 